import React, { Suspense } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { GlobalErrorFallback } from "@/components/GlobalErrorBoundary";
import { LoadingSplash } from "@/components/ui/LoadingSplash";
import { reportToRollbar } from "@/lib/rollbarReport";

/**
 * RouteSuspense — the standard firewall around every lazy-loaded route.
 *
 * It stacks two concerns that always travel together for a lazy route:
 *
 *   1. <Suspense> — shows `LoadingSplash` while the route's JS chunk is
 *      being fetched and parsed.
 *   2. <ErrorBoundary> — catches three very different error shapes:
 *
 *        • A `ChunkLoadError` (thrown by the webpack/rollup runtime when a
 *          chunk URL 404s because we shipped a new deploy and the user's
 *          HTML still references the old hash). The correct UX here is
 *          NOT a red error screen — it's a silent soft reload that pulls
 *          the fresh `index.html` and therefore the fresh chunk hashes.
 *
 *        • A `TypeError: Failed to fetch dynamically imported module`
 *          (the same failure, but surfaced by native `import()` in Vite's
 *          production build). Handled identically.
 *
 *        • Any other error — rendered through `GlobalErrorFallback`, the
 *          full-screen "service interruption" UI.
 *
 * Wrap every lazy page with this instead of a bare `Suspense`.
 */

/**
 * Returns true if the error was produced by the browser failing to fetch a
 * code-split chunk — almost always because the user is on a stale tab
 * after a new deploy. Checks both the webpack-ish `ChunkLoadError` name
 * and the native `import()` failure message, and — defensively — the
 * common "Loading chunk N failed" variants.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const err = error as { name?: unknown; message?: unknown };
  const name    = typeof err.name    === "string" ? err.name    : "";
  const message = typeof err.message === "string" ? err.message : "";

  if (name === "ChunkLoadError") return true;
  if (/Loading chunk [\d]+ failed/i.test(message)) return true;
  if (/Failed to fetch dynamically imported module/i.test(message)) return true;
  if (/Importing a module script failed/i.test(message)) return true;

  return false;
}

/**
 * Guard: never soft-reload in a tight loop. If the chunk is genuinely
 * unreachable (network down, bad deploy), reloading forever would brick
 * the tab. We record the attempt in sessionStorage and skip the reload
 * the second time, falling through to the normal error UI.
 */
const RELOAD_GUARD_KEY = "__orf_chunk_reload_at__";
const RELOAD_COOLDOWN_MS = 10_000;

function shouldSoftReload(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    const last = raw ? Number(raw) : 0;
    if (Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    return true;
  } catch {
    // sessionStorage can throw in private-mode Safari / blocked storage.
    // Fall through to a single reload attempt — better UX than a dead page.
    return true;
  }
}

function RouteLoadErrorFallback(props: FallbackProps) {
  const { error } = props;

  React.useEffect(() => {
    if (isChunkLoadError(error) && shouldSoftReload()) {
      // Soft reload: strip any hash or search param so the browser
      // re-requests the canonical URL and picks up the new index.html.
      window.location.reload();
    }
  }, [error]);

  // If this IS a chunk error, the effect above will reload immediately.
  // Render the splash in the meantime so the user sees "Loading Module"
  // instead of an error flash mid-reload.
  if (isChunkLoadError(error)) {
    return <LoadingSplash />;
  }

  return <GlobalErrorFallback {...props} />;
}

export interface RouteSuspenseProps {
  children: React.ReactNode;
  /**
   * Optional custom fallback while the chunk is downloading. Defaults to
   * the global `LoadingSplash`.
   */
  fallback?: React.ReactNode;
}

export function RouteSuspense({ children, fallback }: RouteSuspenseProps) {
  return (
    <ErrorBoundary
      FallbackComponent={RouteLoadErrorFallback}
      onError={(error, info) => {
        console.error(
          "[RouteSuspense]",
          error,
          info.componentStack,
        );
        // Stale deploy chunk fetches are expected; don't spam Rollbar.
        if (!isChunkLoadError(error)) {
          reportToRollbar(error, {
            tags:  { boundary: "route-suspense" },
            extra: { componentStack: info.componentStack },
          });
        }
      }}
      onReset={() => {
        // For non-chunk errors, a hard reload is the safe default — we
        // don't know what state the app is in after an arbitrary render
        // failure in the route tree.
        window.location.reload();
      }}
    >
      <Suspense fallback={fallback ?? <LoadingSplash />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}
